'use client'

import { ReactNode, useMemo, useEffect, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { CacheProvider } from '@emotion/react'
import { EmotionCache } from '@emotion/cache'
import { themeCreator } from '@/app/components/themes/base'
import createEmotionCache from '@/createEmotionCache'
import { useLayout } from './LayoutProvider'

interface MuiThemeProviderProps {
  children: ReactNode
  brandName?: string
  emotionCache?: EmotionCache
}

export default function MuiThemeProvider({ 
  children, 
  brandName: initialBrandName = 'nestingale',
  emotionCache 
}: MuiThemeProviderProps) {
  const layout = useLayout()
  const [currentBrand, setCurrentBrand] = useState(initialBrandName)
  
  // Update brand when it changes in layout context
  useEffect(() => {
    const brandFromContext = layout?.brand?.vendorName || layout?.brand?.name
    if (brandFromContext) {
      setCurrentBrand(brandFromContext)
    }
  }, [layout?.brand])
  
  // Create client-side cache only when needed
  const clientCache = useMemo(() => createEmotionCache(), [])
  const cache = emotionCache || clientCache
  
  const theme = useMemo(() => themeCreator(currentBrand + 'Theme'), [currentBrand])
  
  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div data-provider="mui-theme" data-brand={currentBrand}>
          {children}
        </div>
      </ThemeProvider>
    </CacheProvider>
  )
}